/**
 * 
 * @author Tim
 * @date 2024-01-05
 */

/**
 * 获取请求头信息对象，包含常见的请求头字段。
 * @returns {Object} 请求头信息对象。
 * @property {string} "content-type" - 请求的内容类型，此处为 JSON。
 * @property {string} "x-app-code" - 应用程序标识，从全局环境变量中获取。
 * @property {string|null} "Authorization" - 用户令牌，从本地存储获取，添加Bearer前缀，可能为 null。
 */
export function getHeaders() {
	const token = getToken();
	return {
		"content-type": 'application/json',
		"x-app-code": process.uniEnv.appID,
		"Authorization": token ? `Bearer ${token}` : ''
	};
}

/**
 * 获取存储在本地的用户令牌（Token）。
 * @returns {string|null} 返回存储的用户令牌，如果不存在则返回 null。
 */
export function getToken() {
	return uni.getStorageSync("TOKEN");
}

/**
 * 将用户令牌（Token）存储在本地。
 * @param {string} token - 要存储的用户令牌。
 */
export function setToken(token) {
	uni.setStorageSync("TOKEN", token);
}

export function getUserInfo() {
	return uni.getStorageSync("USER_INFO");
}

/**
 * 获取Token时间戳
 * @returns {number|null} Token创建时间戳，不存在则返回null
 */
export function getTokenTime() {
	const tokenTime = uni.getStorageSync("TOKEN_TIME");
	return tokenTime ? parseInt(tokenTime, 10) : null;
}

/**
 * 设置Token时间戳
 * @param {number} timestamp - 时间戳
 */
export function setTokenTime(timestamp) {
	uni.setStorageSync("TOKEN_TIME", timestamp.toString());
}

/**
 * 检查Token是否有效
 * @param {number} validDuration - 有效期时长(毫秒)，默认5.5小时
 * @returns {object} 检查结果 {isValid: boolean, tokenAge: number, remainingTime: number}
 */
export function checkTokenValidity(validDuration = 5.5 * 60 * 60 * 1000) {
	const token = getToken();
	const tokenTime = getTokenTime();
	
	if (!token || !tokenTime) {
		return { isValid: false, tokenAge: 0, remainingTime: 0 };
	}
	
	const now = Date.now();
	const tokenAge = now - tokenTime;
	const remainingTime = validDuration - tokenAge;
	
	return {
		isValid: remainingTime > 0,
		tokenAge,
		remainingTime: Math.max(0, remainingTime)
	};
}

/**
 * 清除所有认证相关数据
 */
export function clearAuthData() {
	const authKeys = ['TOKEN', 'TOKEN_TIME', 'USER_INFO'];
	
	authKeys.forEach(key => {
		try {
			uni.removeStorageSync(key);
		} catch (error) {
			console.error(`清除存储键 ${key} 失败:`, error);
		}
	});
	
	console.log('🗑️ 已清除所有认证数据');
}

/**
 * 批量设置认证数据
 * @param {string} token - 用户Token
 * @param {object} userInfo - 用户信息
 * @param {number} timestamp - 时间戳，默认为当前时间
 */
export function setAuthData(token, userInfo, timestamp = Date.now()) {
	try {
		uni.setStorageSync('TOKEN', token);
		uni.setStorageSync('TOKEN_TIME', timestamp.toString());
		uni.setStorageSync('USER_INFO', userInfo);
		console.log('✅ 认证数据已保存');
	} catch (error) {
		console.error('❌ 保存认证数据失败:', error);
		throw error;
	}
}